import { InvalidCatalogDefinitionError } from "./errors.model";
import {
  assignMemberIds,
  highWaterMark,
  type Member,
  type MemberId,
  memberIdSequence,
  memberIds,
} from "./member-identity";
import {
  assertUnpinnedReference,
  CatalogDefinition,
  type CatalogSpine,
  type ReferenceAvailabilityFacts,
} from "./spine.model";

export type ProgramTimeWindow = "early_morning" | "mid_morning" | "midday" | "early_afternoon" | "late_afternoon";
declare const programProtocolRefBrand: unique symbol;
/** An unpinned canonical URL naming a ProtocolDefinition lineage. */
export type ProgramProtocolRef = string & { readonly [programProtocolRefBrand]: true };

const PROGRAM_TIME_WINDOWS = new Set<string>([
  "early_morning",
  "mid_morning",
  "midday",
  "early_afternoon",
  "late_afternoon",
]);

export function programProtocolRef(url: string): ProgramProtocolRef {
  const lineage = assertUnpinnedReference(url);
  if (!URL.canParse(lineage)) {
    throw new InvalidCatalogDefinitionError(`Program protocol url '${lineage}' is not canonical`);
  }
  return lineage as ProgramProtocolRef;
}

export function programTimeWindow(value: string): ProgramTimeWindow {
  if (!PROGRAM_TIME_WINDOWS.has(value)) {
    throw new InvalidCatalogDefinitionError(`Program time window '${value}' is invalid`);
  }
  return value as ProgramTimeWindow;
}

export interface ProgramSession {
  id: MemberId;
  timeWindow: ProgramTimeWindow;
  /** ProtocolDefinition lineage; its current revision resolves only when read or materialized. */
  protocol: ProgramProtocolRef;
}

export interface ProgramDay {
  id: MemberId;
  sessions: readonly ProgramSession[];
}

export interface ProgramSessionInput extends Member {
  timeWindow: ProgramTimeWindow;
  protocol: ProgramProtocolRef;
}

export interface ProgramDayInput extends Member {
  sessions: readonly ProgramSessionInput[];
}

export interface ProgramDefinitionProps {
  spine: CatalogSpine;
  days: readonly ProgramDayInput[];
  dayHighWaterMark?: number;
  sessionHighWaterMark?: number;
}

export interface ProgramDefinitionFacts {
  spine: CatalogSpine;
  days: readonly ProgramDay[];
  dayHighWaterMark: number;
  sessionHighWaterMark: number;
}

/** A multi-day composition of redesigned ProtocolDefinition lineages. */
export class ProgramDefinition extends CatalogDefinition<ProgramDefinition> {
  private programDays: readonly ProgramDay[];
  private dayHigh: number;
  private sessionHigh: number;

  constructor(props: ProgramDefinitionProps) {
    super(props.spine);
    this.dayHigh = Math.max(props.dayHighWaterMark ?? 0, highWaterMark(props.days));
    this.sessionHigh = Math.max(
      props.sessionHighWaterMark ?? 0,
      highWaterMark(props.days.flatMap((day) => day.sessions)),
    );
    const assigned = this.assignDays(
      props.days,
      props.days,
      props.days.flatMap((day) => day.sessions),
    );
    assertDays(assigned.days, props.spine.url);
    this.programDays = assigned.days;
    this.dayHigh = assigned.dayHigh;
    this.sessionHigh = assigned.sessionHigh;
  }

  get days(): readonly ProgramDay[] {
    return structuredClone(this.programDays);
  }

  get protocolLineages(): readonly ProgramProtocolRef[] {
    return [...new Set(this.programDays.flatMap((day) => day.sessions.map((session) => session.protocol)))];
  }

  snapshot(): ProgramDefinitionFacts {
    return this.facts;
  }

  get facts(): ProgramDefinitionFacts {
    return {
      spine: this.spineFacts,
      days: this.days,
      dayHighWaterMark: this.dayHigh,
      sessionHighWaterMark: this.sessionHigh,
    };
  }

  setDays(days: readonly ProgramDayInput[], facts: ReferenceAvailabilityFacts): void {
    this.assertReferencesActive(facts);
    const assigned = this.assignDays(
      days,
      this.programDays,
      this.programDays.flatMap((day) => day.sessions),
    );
    assertDays(assigned.days, this.url);
    this.programDays = assigned.days;
    this.dayHigh = assigned.dayHigh;
    this.sessionHigh = assigned.sessionHigh;
  }

  protected copyForRevision(spine: CatalogSpine): ProgramDefinition {
    return new ProgramDefinition({
      spine,
      days: this.programDays,
      dayHighWaterMark: this.dayHigh,
      sessionHighWaterMark: this.sessionHigh,
    });
  }

  protected substantiveFacts(): unknown {
    return this.programDays;
  }

  private assignDays(
    incoming: readonly ProgramDayInput[],
    knownDays: readonly ProgramDayInput[],
    knownSessions: readonly ProgramSessionInput[],
  ): { days: ProgramDay[]; dayHigh: number; sessionHigh: number } {
    const daySequence = memberIdSequence("day", this.dayHigh);
    const sessionSequence = memberIdSequence("session", this.sessionHigh);
    const days = assignMemberIds(incoming, memberIds(knownDays), daySequence, "program day");
    const sessions = assignMemberIds(
      days.flatMap((day) => day.sessions),
      memberIds(knownSessions),
      sessionSequence,
      "program session",
    );
    let cursor = 0;
    const assignedDays = days.map((day) => ({
      ...day,
      id: day.id as MemberId,
      sessions: day.sessions.map(() => sessions[cursor++] as ProgramSession),
    }));
    return { days: assignedDays, dayHigh: daySequence.high, sessionHigh: sessionSequence.high };
  }
}

function assertDays(days: readonly ProgramDay[], url: string): void {
  if (days.length === 0) {
    throw new InvalidCatalogDefinitionError("A program must have at least one day", { url });
  }
  days.forEach((day, dayIndex) => {
    if (day.sessions.length === 0) {
      throw new InvalidCatalogDefinitionError(`Program day ${dayIndex + 1} must have at least one session`, { url });
    }
    day.sessions.forEach(({ protocol, timeWindow }) => {
      try {
        programProtocolRef(protocol);
        programTimeWindow(timeWindow);
      } catch (error) {
        if (error instanceof InvalidCatalogDefinitionError) {
          throw new InvalidCatalogDefinitionError(error.message, { url });
        }
        throw error;
      }
    });
  });
}
